/**
 * UI Component Colors
 * All reusable UI components: buttons, inputs, dropdowns, checkboxes, search, calendar
 */
import { palette, createColor } from './palette.colors';

// Button colors
export const buttonColors = {
  primary: createColor(palette.blue.primary),
  primaryText: createColor(palette.gray.white),
  secondary: createColor(palette.gray.white, palette.gray.white), // white bg — secondary button default has white fill
  secondaryText: createColor(palette.gray.offBlack, palette.dark.textAlt), // #09090B — dark text
  secondaryBorderColor: createColor(palette.blue.selectedBorder, palette.blue.selectedBorder), // #66ABFF
  secondaryHoverBG: createColor(palette.blue.selectedItemBG, palette.dark.border),  // #E6F1FF hover fill
  secondaryPressedBG: createColor(palette.blue.pale, palette.dark.borderLight),     // #D9EBF6 pressed fill
  disabled: createColor(palette.gray.disabled),
  disabledText: createColor(palette.gray.slate),
  ctaDisableBG: createColor(palette.gray.disabledCTABG, palette.gray.lightDarkGrey),  // #CED8E3
  ctaDisableText: createColor(palette.gray.disabledCTAText, palette.gray.tabDisabledText),         // #FFFFFF
  borderColor: createColor(palette.gray.mainBorder, palette.dark.techBorder),
  CTAIcon: createColor(palette.blue.onTint, palette.dark.text),
  CTAText: createColor(palette.blue.onTint, palette.dark.text),
  /** Badge pill shown beside button title (e.g. task count) */
  subTitleBadgeBg: createColor(palette.blue.badge, palette.blue.badge),   // #004EA8
  subTitleBadgeText: createColor(palette.gray.white, palette.blue.selectedText), // #FFFFFF
  badgeDisabledText: createColor(palette.gray.darkGreyLight, palette.gray.darkGreyLight)
};

// Input field colors
export const inputColors = {
  border: createColor(palette.gray.mainBorder, palette.gray.darkGreyLight),
  disabledBorder: createColor(palette.gray.disabledNeutral, palette.gray.lightDarkGrey),
  BG: createColor(palette.gray.white, 'transparent'),
  placeholder: createColor(palette.gray.placeholderText, palette.gray.zinc),
  placeHolder: createColor(palette.gray.zinc, palette.gray.zinc),
  textColor: createColor(palette.gray.black, palette.gray.black),
  color: createColor(palette.gray.darkest, palette.dark.textAlt),
  diabledBG: createColor(palette.gray.disabledBG, palette.gray.lightDarkGrey),
  diabledText: createColor(palette.blue.onTint, palette.gray.tabDisabledText),
  /** Focus (active) border — palette.blue.selectedText (#005FCC) */
  activeBorder: createColor(palette.blue.selectedText),
  /** Hover border — palette.blue.selectedBorder (#66ABFF) */
  hoverBorder: createColor(palette.blue.selectedBorder),
};

// Checkbox colors
export const checkboxColors = {
  border: createColor(palette.gray.black, palette.gray.white),
  label: createColor(palette.blue.onTint, palette.gray.white),
  selectedColor: createColor(palette.blue.secondary),
  unSelectedColor: createColor(palette.gray.dark),

  // ── Normal (unchecked) ──────────────────────────────────────────────────
  /** Unchecked bg — white fill (Figma Normal state) */
  unSelectedBGColor: createColor(palette.gray.white, 'transparent'),
  /** Unchecked border — #66ABFF (Figma Normal state: Button Outline) */
  unSelectedBorderColor: createColor(palette.blue.selectedBorder, palette.blue.selectedBorder),

  // ── Checked ─────────────────────────────────────────────────────────────
  /** Checked bg — #005FCC solid primary (Figma Checked state: Button Fill) */
  selectedBGColor: createColor(palette.blue.selectedText, palette.blue.selectedText),
  /** Checked border — none (Figma Checked state: no outline) */
  selectedBorderColor: createColor(palette.special.transparent, palette.special.transparent),

  // ── Hover (unchecked) ───────────────────────────────────────────────────
  /** Hover bg — #E6F1FF light tint (Figma Hover state: Button Fill) */
  hoverBGColor: createColor(palette.blue.selectedItemBG, palette.blue.selectedItemBG),
  /** Hover border — #66ABFF (Figma Hover state: Button Outline) */
  hoverBorderColor: createColor(palette.blue.selectedBorder, palette.blue.selectedBorder),

  // ── Pressed (unchecked) ─────────────────────────────────────────────────
  /** Pressed bg — #CCE3FF medium tint (Figma Pressed state: Button Fill) */
  pressedBGColor: createColor(palette.borders.dropdownBlue, palette.borders.dropdownBlue),
  /** Pressed border — #66ABFF (Figma Pressed state: Button Outline) */
  pressedBorderColor: createColor(palette.blue.selectedBorder, palette.blue.selectedBorder),

  // ── Disabled (unchecked) ────────────────────────────────────────────────
  /** Disabled bg — #F1F5F9 slate-100 (Figma Disabled state: Button Fill) */
  disabledBGColor: createColor('#F1F5F9', '#F1F5F9'),
  /** Disabled border — #CED8E3 muted blue-gray (Figma Disabled state: Button Outline) */
  disabledBorderColor: createColor(palette.gray.tabDisabled, palette.gray.tabDisabled),

  // ── Checked + Disabled ──────────────────────────────────────────────────
  /** Checked-disabled bg — #CED8E3 (Figma Checked+Disabled: Button Fill) */
  checkedDisabledBGColor: createColor(palette.gray.tabDisabled, palette.gray.tabDisabled),
  /** Checked-disabled border — none (Figma Checked+Disabled: no outline) */
  checkedDisabledBorderColor: createColor(palette.special.transparent, palette.special.transparent),

  // ── Tick icon ───────────────────────────────────────────────────────────
  tickMarkIconColor: createColor(palette.gray.white, palette.gray.white),
};

// Dropdown colors
export const dropdownColors = {
  icon: createColor(palette.blue.onTint, palette.dark.text),
  BGColor: createColor(palette.gray.white, palette.gray.black),
  mainBGColor: createColor(palette.gray.white, palette.special.transparent),
  selectedBGColor: createColor(palette.blue.selectedItemBG, palette.blue.pressed),
  selectedTextColor: createColor(palette.blue.selectedText, palette.blue.light),
  textColor: createColor(palette.blue.onTint, palette.gray.white),
  selectedTickColor: createColor(palette.blue.selectedText, palette.blue.light),
  shadorColor: createColor(palette.opacity.shadowBlue),
  selecetedBorderColor: createColor(palette.borders.selected, palette.dark.techBorder),
  border: createColor(palette.borders.dropdownBlue, palette.borders.tech),
  openBorder: createColor(palette.borders.dropdownBlue, palette.borders.dropdownBlue),
  hoverBGColor: createColor(palette.blue.selectedItemBG, palette.dark.border),
  disabledBorder: createColor(palette.gray.disabled, palette.gray.darkGreyLight),
  shadowColor: createColor(palette.opacity.black40),
  title: createColor(palette.blue.onTint, palette.gray.white),
  disabledBG: createColor(palette.gray.tabDisabled, palette.gray.lightDarkGrey),
  searchBorder: createColor(palette.gray.divider, palette.borders.tech),
  dropdownSearchBorder: createColor(palette.gray.border, palette.borders.tech),
};

// Search input colors
export const searchInputColors = {
  input: createColor(palette.gray.white, palette.special.transparent),
  border: createColor(palette.gray.neutral, palette.gray.darkGreyLight),
  iconColor: createColor(palette.gray.placeholderText, palette.dark.textAlt),
  resultColor: createColor(palette.blue.onTint, palette.gray.white),
  resultBGColor: createColor(palette.gray.lightWhite, palette.gray.darkGreyLight),
  color: createColor(palette.gray.dark, palette.dark.textAlt),
  placeholder: createColor(palette.gray.placeholderText, palette.dark.textAlt),
  /** Typed text color inside the search input (#4F6178 steel-blue gray) */
  inputColor: createColor(palette.gray.searchResult, palette.gray.white),
};

// Calendar colors
export const calenderColors = {
  textColor: createColor(palette.gray.darker, palette.gray.white),
  disabledText: createColor(palette.gray.dark, palette.gray.tabDisabledText),
  todayTextColor: createColor(palette.blue.primary, palette.blue.selectedBorder),
  timeActiveColor: createColor(palette.gray.white, palette.gray.white),
  timeInActiveColor: createColor(palette.gray.white, palette.gray.white),
};

// Profile
export const profileColors = {
  // WCAG 1.4.3: brightGreen #00E304 had 1.75:1 contrast on white → darkGreen #008236 gives 4.6:1
  online: createColor(palette.semantic.darkGreen, palette.semantic.online),
  offline: createColor(palette.semantic.errorRed, palette.gray.tabDisabledText),
  BGColor: createColor(palette.blue.primary, palette.blue.primary),
  textColor: createColor(palette.gray.white, palette.gray.white),

  dropdownBG: createColor(palette.gray.white, palette.blue.softBlue),
  dropdownBorder: createColor(palette.gray.divider, palette.dark.techBorder),
  dropdownShadow: createColor(palette.ui.shadow, palette.ui.shadow),
  dropdownSeparator: createColor(palette.gray.divider, palette.dark.techBorder),
  dropdownUserName: createColor(palette.gray.darkest, palette.gray.white),
  // WCAG 1.4.3: #707070 had 4.48:1 contrast on white (fails for small text) → #595959 gives 7.0:1
  dropdownUserId: createColor(palette.gray.accessibleMid, palette.gray.white),
  dropdownValues: createColor(palette.gray.darkest, palette.dark.textAlt),
  // WCAG 1.4.3: #707070 had 4.48:1 contrast on white (fails for UI components) → #595959 gives 7.0:1
  dropdownValuesIcon: createColor(palette.gray.accessibleMid, palette.gray.accessibleMid),
  logout: createColor(palette.semantic.errorRed, palette.semantic.accumulatedErrorMainText),
  
};

// General UI elements
export const uiColors = {
  border: createColor(palette.gray.border, palette.dark.borderAlt),
  selectedTab: createColor(palette.blue.sky, palette.dark.border),
  selectedTabBorder: createColor(palette.borders.selected, palette.borders.selected),
  tabBorder: createColor(palette.gray.borderLight, palette.dark.techBorder),
  tabDisabledText: createColor(palette.gray.darkest, palette.dark.textAlt),
  tabDisabledBg: createColor(palette.gray.tabDisabled, palette.gray.tabDisabled),
  tabDisabledIconColor: createColor(palette.gray.disabledCTAText, palette.gray.disabledCTAText),
  tabHoverBg: createColor(palette.blue.sky, palette.dark.border),
  tabPressedBg: createColor(palette.blue.pale, palette.dark.borderLight),
  selectedIconColor: createColor(palette.gray.darkest, palette.gray.white),
  iconColor: createColor(palette.gray.dark, palette.gray.white),
  iconColorAlt: createColor(palette.blue.onTint, palette.gray.white),
  menuIconText: createColor(palette.blue.onTint, palette.gray.white),
  shadowColor: createColor(palette.special.shadow),
  cardBorder: createColor(palette.gray.borderLight, palette.borders.tech),
};

export const ModalColors = {
  background: createColor(palette.gray.white, palette.blue.softBlue),
  border: createColor(palette.gray.border, palette.gray.lightDarkGrey),
  title: createColor(palette.blue.onTint, palette.gray.white),
  closeIconColor: createColor(palette.blue.onTint, palette.gray.white),
  separator: createColor(palette.gray.border, palette.dark.techBorder),
  itemSeparator: createColor(palette.gray.separatorGreyBorder, palette.dark.techBorder),
  bottomSeparator: createColor(palette.gray.bottomGreyBorder, palette.dark.techBorder),
};

export const errorToast = {
  // Error toast — Figma: error-secondary/200 bg, error-secondary/500 border & icon
  border: createColor(palette.semantic.dangerRed, palette.semantic.errorSecondary500),             // #EB1A28
  bgColor: createColor(palette.backgrounds.errorSecondary200, palette.backgrounds.errorToastBG), // #FACBCF
  closeIconColor: createColor(palette.semantic.dangerRed, palette.semantic.dangerRed),     // #EB1A28
  closeCTAColor: createColor(palette.gray.dark, palette.gray.dark),
  // Success toast — Figma: success/200 bg, success/500 border & icon
  successBorder: createColor(palette.semantic.success500, palette.semantic.darkSuccessBorder),     // #10B981
  successBgColor: createColor(palette.semantic.success200, palette.semantic.darkSuccessGreen),   // #C8F0E1
  successCloseIconColor: createColor(palette.semantic.success500, palette.semantic.success500), // #10B981
  successCloseCTAColor: createColor(palette.semantic.success500, palette.semantic.success500),  // #10B981
  text: createColor(palette.gray.darkest, palette.semantic.accumultedErrorText),
  successText: createColor(palette.gray.darkest, palette.gray.white),
};

export const feedback = {
  icon: createColor(palette.ui.disabled, palette.ui.disabled),
  title: createColor(palette.blue.onTint, palette.gray.white),
  subTitle: createColor(palette.blue.onTint, palette.dark.textAlt),
}
